<template>
  <div class="asset-info-form">
    <KycDropdown
      title="Asset Information"
      subTitle="Fill in necessary asset information"
      :status="orgDetails.tokens?.length ? true : false"
      :edit="''"
      @active-dropdown="setDropdownStatus"
    >
      <template v-slot:content>
        <form @submit.prevent="uploadImage" class="form-content">
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Asset Image</label>
              <DragNDrop
                accept="image/png, image/jpeg, image/jpg, .pdf"
                :currentValue="assetInfoPayload?.image"
                :startUpload="uploadTokenImage"
                :validate="imageValid"
                @reupload="uploadTokenImage = false"
                @file-added="setImage"
                @upload-complete="submitAssetInfo"
                @uploadError="uploadImageError"
              />
            </div>
            <div class="form-group">
              <TextAreaInput
                placeHolder="Write something about your company."
                label="Profile Summary"
                name="company-summary"
                id="company-summary"
                :text="assetInfoPayload.about"
                :showLabel="true"
                :maxlength="500"
                :rows="8"
                @set="setCompanySummary"
                @valid="companySummaryValid"
              />
            </div>
          </div>

          <div class="section-divider">
            <span class="section-title">About Asset</span>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <SelectInput
                label="Industry"
                defaltOption="Select industry"
                name="industry"
                id="industry"
                :showLabel="true"
                :option="assetInfoPayload.industry"
                :list="industries"
                @set="setIndustries"
              />
            </div>
            <div class="form-group">
              <TextInput
                placeHolder="Asset name"
                label="Asset name"
                type="text"
                fieldType="text"
                id="asset_name"
                name="asset_name"
                :showLabel="true"
                :text="assetInfoPayload.name"
                @set="setAssetName"
                @valid="assetNameValid"
              />
            </div>
            <div class="form-group">
              <SelectInput
                label="Company type"
                defaltOption="Select company type"
                name="company_type"
                id="company_type"
                :showLabel="true"
                :option="assetInfoPayload.company_type"
                :list="companyType"
                @set="setCompanyType"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Date of Incorporation</label>
              <Datepicker
                v-model="dateOfIncorporation"
                :inputFormat="'yyyy-MM-dd'"
                :upperLimit="upperLimit"
                placeholder="Enter date of Incorporation"
                name="dateOfIncorporation"
                class="date-input"
                required
              />
            </div>
            <div class="form-group">
              <SelectInput
                label="Stage"
                defaltOption="Select stage"
                name="stage"
                id="stage"
                :showLabel="true"
                :option="assetInfoPayload.stage"
                :list="seedStage"
                @set="setStage"
              />
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="assetInfoLoading">
              <span v-if="assetInfoLoading" class="btn-loader"></span>
              {{ $route.name === 'EsopDetails' ? 'Save Changes' : 'Continue' }}
            </button>
          </div>
        </form>
      </template>
    </KycDropdown>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from "vuex";
  import Datepicker from "vue3-datepicker";
  import KycDropdown from "@/components/general/KycDropdown";
  import DragNDrop from "@/components/general/DragNDrop";
  import TextAreaInput from "@/components/general/TextAreaInput";
  import TextInput from "@/components/general/TextInput";
  import SelectInput from "@/components/general/SelectInput";
  import Btn from "@/components/general/BtnComponent";
  import { getItem } from "@/utils/storage.js";
  import { industries } from "@/utils/helpers";

  export default {
    name: "AssetInfoForm",

    components: {
      Datepicker,
      KycDropdown,
      DragNDrop,
      TextAreaInput,
      TextInput,
      SelectInput,
      Btn,
    },

    data() {
      return {
        industries,
        assetInfoLoading: false,
        imageValid: false,
        validInput: {
          companySummary: false,
          assetName: false,
        },
        assetInfoPayload: {
          about: "",
          company_type: "",
          dateOfIncorporation: "",
          image: "",
          industry: "",
          name: "",
          stage: "",
          type: "Company",
        },
        uploadTokenImage: false,
        dateOfIncorporation: new Date(undefined),
        upperLimit: new Date(),
        companyType: ["Public", "Private", "Non Profit"],
      };
    },

    computed: {
      ...mapState({
        orgDetails: (state) => state.organisationModule.organisationDetails,
        assetDetails: (state) => state.assetModule.assetDetails,
      }),
      ...mapGetters("organisationModule", ["lastRaiseStage"]),
      seedStage() {
        const allSeedStages = [
          "Pre seed",
          "Seed",
          "Series A",
          "Series B",
          "Series C",
          "Series D",
          "Public",
        ];
        const seedStage =
          this.$route.name === "CreateAssetInfo" &&
          this.$route.params.type === "private-raise"
            ? allSeedStages.filter((stage) => stage !== this.lastRaiseStage)
            : allSeedStages;
        return seedStage;
      },
    },

    mounted() {
      this.fetchAsset();
    },

    methods: {
      ...mapActions("assetModule", ["createAsset", "getAsset", "updateAsset"]),
      ...mapActions(["showToast"]),

      setDropdownStatus(title) {
        this.$emit("dropdown-title", title);
      },

      setImage(url) {
        this.assetInfoPayload.image = url;
      },

      setAssetName(text) {
        this.assetInfoPayload.name = text;
      },

      setIndustries(option) {
        this.assetInfoPayload.industry = option;
      },

      setCompanyType(option) {
        this.assetInfoPayload.company_type = option;
      },

      setStage(option) {
        this.assetInfoPayload.stage = option;
      },

      setCompanySummary(text) {
        this.assetInfoPayload.about = text;
      },

      assetNameValid(valid) {
        this.validInput.assetName = valid;
      },

      companySummaryValid(valid) {
        this.validInput.companySummary = valid;
      },

      async submitAssetInfo(url) {
        this.assetInfoPayload.image = url;
        if (
          !this.$route.query.asset &&
          this.$route.name !== "ConfigurePrivateRaise"
        ) {
          if (this.$route.params.type === "employee-benefits") {
            this.assetInfoPayload.token_type = "ESO_TOKEN";
          }
          // if (this.$route.params.type === "public-raise") {
          //   this.assetInfoPayload.token_type = "";
          // }
          const response = await this.createAsset(this.assetInfoPayload);
          if (response) {
            this.updateTokenInfo();
            this.$emit("dropdown-title", "Investment Memo");
          }
          this.assetInfoLoading = !this.assetInfoLoading;
          this.uploadTokenImage = false;
        } else {
          const response = await this.updateAsset({
            data: this.assetInfoPayload,
            id: this.$route.query.asset
              ? this.$route.query.asset
              : this.assetDetails._id,
          });
          if (response) {
            this.updateTokenInfo();
            this.$emit("dropdown-title", "Investment Memo");
          }
          this.assetInfoLoading = !this.assetInfoLoading;
          this.uploadTokenImage = false;
        }
      },

      uploadImageError() {
        this.assetInfoLoading = false;
        this.uploadTokenImage = false;
      },

      uploadImage() {
        setTimeout(() => {
          if (
            !this.validInput.companySummary ||
            !this.validInput.assetName ||
            !this.assetInfoPayload.industry ||
            !this.assetInfoPayload.dateOfIncorporation ||
            !this.assetInfoPayload.company_type ||
            !this.assetInfoPayload.about ||
            !this.assetInfoPayload.image
          ) {
            this.imageValid = true;
            return this.showToast({
              description: "Please fill all input field correctly",
              display: true,
              type: "error",
            });
          }
          this.assetInfoLoading = true;
          this.uploadTokenImage = true;
        }, 500);
      },

      async fetchAsset() {
        this.assetInfoPayload.name = getItem("asset_name");
        this.assetInfoPayload.industry = this.orgDetails?.industry;
        if (!this.$route.query.asset && this.orgDetails.dateOfIncorporation) {
          const dateOfIncorporation =
            this.orgDetails.dateOfIncorporation?.split("T")[0];
          this.assetInfoPayload.about = this.orgDetails?.about;
          this.assetInfoPayload.image = this.orgDetails?.image;
          this.dateOfIncorporation = new Date(dateOfIncorporation);
          this.assetInfoPayload.stage = this.orgDetails?.stage;
          return;
        } else if (this.$route.query.asset) {
          await this.getAsset(this.$route.query.asset);
          this.updateTokenInfo();
        }
      },

      updateTokenInfo() {
        this.assetInfoPayload.about = this.assetDetails?.about;
        this.assetInfoPayload.company_type = this.assetDetails?.company_type;
        this.dateOfIncorporation = new Date(
          this.assetDetails?.dateOfIncorporation
        );
        this.assetInfoPayload.image = this.assetDetails?.image;
        this.assetInfoPayload.industry = this.assetDetails?.industry;
        this.assetInfoPayload.name = this.assetDetails?.name;
        this.assetInfoPayload.stage = this.assetDetails?.stage;
      },
    },

    watch: {
      assetDetails(newValue, oldValue) {
        deep: true;
        immediate: true;
        if (newValue !== oldValue) {
          this.updateTokenInfo();
        }
      },
      dateOfIncorporation(val, old) {
        deep: true;
        immediate: true;
        if (String(val) !== String(old)) {
          // this.dateOfIncorporation = new Date(val);
          this.assetInfoPayload.dateOfIncorporation = new Date(val)
            .toISOString()
            .split("T")[0];
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
.asset-info-form {
  .form-content {
    padding: 8px 0;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }

  .section-divider {
    border-top: 1px solid #E2E8F0;
    padding-top: 24px;
    margin-bottom: 20px;
  }

  .section-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }

  .date-input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #0F172A;
    background: #FFFFFF;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #4D84FF;
    }

    &::placeholder {
      color: #94A3B8;
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 16px;
    border-top: 1px solid #E2E8F0;
    margin-top: 8px;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 44px;
    padding: 0 24px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #FFFFFF;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
      background: #3A6FE8;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .btn-loader {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #FFFFFF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
